import React, { useMemo, useState } from "react";
import { Box, Button, Flex, Icon, Input, Text } from "@chakra-ui/react";
import { HiOutlineXMark, HiOutlineLockClosed, HiOutlineLockOpen } from "react-icons/hi2";
import { c, popupSurface } from "../../classroomColors";
import { STANDE } from "../../standeTokens";
import { KIND_LABEL } from "./useLiveActivity";
import { Chip, Countdown, Eyebrow, Initial, MarkPill, QuestionBody, blueButton, inkButton, quietButton } from "./parts";

/** An escape room's locks, and the door. Shared by the big screen and every
 *  student's: the class works the same room. `onOpenLock` makes a shut lock
 *  something to answer. */
export const EscapeBoard = ({ activity, onOpenLock, onUnlock, unlockError }) => {
  const [code, setCode] = useState("");
  const locks = activity.locks || [];
  const cracked = locks.filter((l) => l.cracked).length;

  if (activity.escaped) {
    const crackers = [...new Set(locks.map((l) => l.cracked_by).filter(Boolean))];
    return (
      <Flex direction="column" align="center" textAlign="center" py={{ base: "30px", md: "60px" }} gap="12px">
        <Eyebrow>Door open</Eyebrow>
        <Text fontSize={{ base: "40px", md: "64px" }} fontWeight="700" color={c.text} lineHeight="1">
          You escaped
        </Text>
        <Text fontSize="15px" color={c.textSub}>
          The class cracked all {locks.length} locks together.
        </Text>
        <Flex gap="8px" mt="10px" wrap="wrap" justify="center">
          {crackers.map((name) => (
            <Initial key={name} name={name} size="44px" />
          ))}
        </Flex>
      </Flex>
    );
  }

  return (
    <Box>
      <Box display="grid" gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }} gap="10px">
        {locks.map((l, i) => {
          const q = activity.questions[i];
          return (
            <Flex
              key={l.question_guid}
              as={!l.cracked && onOpenLock ? "button" : "div"}
              type={!l.cracked && onOpenLock ? "button" : undefined}
              onClick={!l.cracked && onOpenLock ? () => onOpenLock(q) : undefined}
              direction="column"
              align="flex-start"
              textAlign="left"
              gap="6px"
              p="16px"
              minH="120px"
              borderRadius="16px"
              bg={l.cracked ? STANDE.lightGreen : c.surface}
              border="1px solid"
              borderColor={l.cracked ? "transparent" : c.borderSoft}
              transition="transform 0.15s, background 0.3s"
              _hover={!l.cracked && onOpenLock ? { transform: "translateY(-2px)", borderColor: c.textMuted } : {}}
            >
              <Flex w="100%" align="center" justify="space-between">
                <Text fontSize="10.5px" letterSpacing="0.1em" color={l.cracked ? "rgba(34,34,34,0.6)" : c.textMuted}>
                  {q?.topic_name ? q.topic_name.toUpperCase() : `LOCK ${i + 1}`}
                </Text>
                <Flex boxSize="32px" borderRadius="full" align="center" justify="center"
                  bg={l.cracked ? STANDE.black : c.infoSoft} color={l.cracked ? STANDE.cream : c.infoText}
                  fontSize="15px" fontWeight="700">
                  {l.cracked ? l.digit : <Icon as={HiOutlineLockClosed} boxSize="15px" />}
                </Flex>
              </Flex>
              <Text fontSize="17px" fontWeight="600" color={l.cracked ? STANDE.black : c.text}>{l.name}</Text>
              <Text fontSize="12.5px" color={l.cracked ? "rgba(34,34,34,0.7)" : c.textSub}>
                {l.cracked
                  ? `Cracked by ${l.cracked_by}${l.seconds != null ? ` · ${l.seconds}s` : ""}`
                  : onOpenLock
                  ? "Locked — answer to crack it"
                  : "Locked"}
              </Text>
            </Flex>
          );
        })}
      </Box>

      <Flex mt="12px" p="16px" borderRadius="16px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}
        align={{ base: "stretch", md: "center" }} direction={{ base: "column", md: "row" }} gap="14px">
        <Box flex="1">
          <Eyebrow mb="8px">Final code</Eyebrow>
          <Flex gap="8px">
            {locks.map((l) => (
              <Flex key={l.question_guid} w="52px" h="60px" borderRadius="12px" align="center" justify="center"
                bg={l.cracked ? STANDE.black : c.surfaceSubtle} color={l.cracked ? STANDE.cream : c.textMuted}
                fontSize="26px" fontWeight="700">
                {l.cracked ? l.digit : "·"}
              </Flex>
            ))}
          </Flex>
          <Text fontSize="12px" color={c.textSub} mt="8px">
            {cracked === locks.length
              ? "Every lock is open. Enter the code."
              : `Crack ${locks.length - cracked} more ${locks.length - cracked === 1 ? "lock" : "locks"} to see the rest of the code.`}
          </Text>
        </Box>
        {onUnlock && (
          <Flex gap="8px" align="center">
            <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, locks.length))}
              placeholder="Enter code" inputMode="numeric" w="150px" h="42px" borderRadius="12px" textAlign="center"
              bg={c.surface} borderColor={c.borderSoft} fontSize="16px" letterSpacing={code ? "0.2em" : "normal"} color={c.text}
              _placeholder={{ color: c.textMuted, fontSize: "14px" }}
              _focusVisible={{ borderColor: c.solidBg, boxShadow: "none" }} />
            <Button {...inkButton} h="42px" borderRadius="12px" fontSize="13px" fontWeight="600"
              leftIcon={<Icon as={HiOutlineLockOpen} boxSize="15px" />}
              isDisabled={code.length !== locks.length} onClick={() => onUnlock(code)}>
              Unlock door
            </Button>
          </Flex>
        )}
      </Flex>
      {unlockError && <Text fontSize="12.5px" color={c.accentSoftText} mt="8px">{unlockError}</Text>}
    </Box>
  );
};

const Leaderboard = ({ rows }) => (
  <Box>
    <Eyebrow mb="10px">Leaderboard</Eyebrow>
    {rows.length === 0 && <Text fontSize="13px" color={c.textMuted}>Points appear as answers are marked.</Text>}
    <Flex direction="column" gap="6px">
      {rows.slice(0, 6).map((r, i) => (
        <Flex key={r.user_guid} align="center" gap="10px" p="9px 12px" borderRadius="12px"
          bg={i === 0 ? c.solidBg : c.surface} color={i === 0 ? c.solidText : c.text} border="1px solid"
          borderColor={i === 0 ? c.solidBg : c.borderSoft}>
          <Text w="18px" fontSize="13px" fontWeight="700" opacity={0.7}>{i + 1}</Text>
          <Text flex="1" fontSize="14px" fontWeight="500" noOfLines={1}>{r.name}</Text>
          <Text fontSize="14px" fontWeight="700" sx={{ fontVariantNumeric: "tabular-nums" }}>{r.points}</Text>
        </Flex>
      ))}
    </Flex>
  </Box>
);

/** The class picture once answers are in: how it went, what most got wrong,
 *  and where to go from here. */
const ClassPicture = ({ activity, act, onReteach, onClear }) => {
  const [questionGuid, setQuestionGuid] = useState(activity.questions[0]?.guid);
  const [finding, setFinding] = useState(false);
  const question = activity.questions.find((q) => q.guid === questionGuid);
  const insight = activity.insights?.[questionGuid];
  const answers = activity.responses.filter((r) => r.question_guid === questionGuid);

  const find = async () => {
    setFinding(true);
    await act((api, s, id) => api.insight(s, id, questionGuid));
    setFinding(false);
  };

  return (
    <Box display="grid" gridTemplateColumns={{ base: "1fr", lg: "1.5fr 1fr" }} gap="16px">
      <Box minW={0}>
        {activity.questions.length > 1 && (
          <Flex gap="6px" mb="12px" wrap="wrap">
            {activity.questions.map((q, i) => (
              <Box key={q.guid} as="button" type="button" onClick={() => setQuestionGuid(q.guid)} px="12px" h="30px"
                borderRadius="200px" fontSize="12px" fontWeight={q.guid === questionGuid ? "600" : "400"}
                bg={q.guid === questionGuid ? c.solidBg : c.surface} color={q.guid === questionGuid ? c.solidText : c.text}
                border="1px solid" borderColor={q.guid === questionGuid ? c.solidBg : c.borderSoft}>
                Question {i + 1}
              </Box>
            ))}
          </Flex>
        )}
        <Flex direction="column" gap="8px" maxH="52vh" overflowY="auto" data-own-scroll pr="2px">
          {answers.length === 0 && <Text fontSize="13px" color={c.textMuted}>No answers to this one.</Text>}
          {answers.map((r) => (
            <Flex key={r.guid} gap="12px" p="12px 14px" borderRadius="14px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
              <Initial name={r.student_name} size="36px" />
              <Box flex="1" minW={0}>
                <Text fontSize="14px" fontWeight="600" color={c.text}>{r.student_name}</Text>
                <Text fontSize="13px" color={c.textSub} noOfLines={2}>{r.final_feedback || r.ai_feedback || r.answer_text}</Text>
              </Box>
              <MarkPill mark={r.final_mark ?? r.ai_mark} total={question?.total_mark || 0} />
            </Flex>
          ))}
        </Flex>
      </Box>

      <Flex direction="column" gap="12px" p="18px" borderRadius="18px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
        <Box>
          <Eyebrow>Class picture</Eyebrow>
          <Text fontSize="46px" fontWeight="700" color={c.text} lineHeight="1.1" mt="4px">
            {activity.average_percent != null ? `${activity.average_percent}%` : "–"}
          </Text>
          <Text fontSize="13px" color={c.textSub}>average across the activity</Text>
        </Box>
        <Box h="1px" bg={c.borderSoft} />
        <Box>
          <Eyebrow mb="6px">Shared misconception</Eyebrow>
          {insight ? (
            <>
              <Text fontSize="14px" color={c.text} lineHeight="1.45">
                {insight.students_affected ? `${insight.students_affected} said: ` : ""}{insight.misconception}
              </Text>
              {insight.reteach && <Text fontSize="12.5px" color={c.textSub} mt="6px">{insight.reteach}</Text>}
            </>
          ) : (
            <Button {...quietButton} size="sm" h="34px" borderRadius="10px" fontSize="12.5px" isLoading={finding}
              loadingText="Reading the answers" onClick={find} isDisabled={!answers.length}>
              Find what most got wrong
            </Button>
          )}
        </Box>
        <Box flex="1" />
        <Button {...blueButton} h="44px" borderRadius="12px" fontSize="14px" fontWeight="600"
          onClick={() => onReteach(question, insight)}>
          Reteach on the board
        </Button>
        <Button {...quietButton} h="40px" borderRadius="12px" fontSize="13px" onClick={onClear}>
          Clear the activity
        </Button>
      </Flex>
    </Box>
  );
};

/**
 * The big screen: what the teacher shows the class, laid over the canvas.
 *
 * During a race, the question and the clock, with the leaderboard once
 * points are in; during an escape room, the locks and the door; at the end,
 * the class picture. Deliberately large and plain — it is read from the
 * back of a room.
 */
const ActivityStage = ({ live, mode, onClose, onReteach, onClear }) => {
  const { activity, act, clock, error } = live;
  const [unlockError, setUnlockError] = useState(null);
  const current = activity?.questions[activity.current_index];
  const answered = useMemo(
    () => (activity?.kind === "QUIZ_RACE" ? activity.answered_counts[current?.guid] || 0 : new Set((activity?.responses || []).map((r) => r.user_guid)).size),
    [activity, current]
  );
  if (!activity) return null;
  const showResults = mode === "results";

  return (
    <Flex position="absolute" inset="0" zIndex={25} align="center" justify="center" p={{ base: "12px", md: "40px" }}
      bg="rgba(34,34,34,0.32)" sx={{ animation: "standeDockIn 0.2s ease-out" }} data-no-pan>
      <Box w="min(1080px, 100%)" maxH="100%" overflowY="auto" p={{ base: "18px", md: "30px" }} borderRadius="26px"
        {...popupSurface} position="relative" data-own-scroll>
        <Flex position="absolute" top="16px" right="16px" as="button" type="button" aria-label="Close the big screen"
          onClick={onClose} boxSize="32px" borderRadius="full" align="center" justify="center" color={c.textSub}
          _hover={{ bg: c.surface, color: c.text }}>
          <Icon as={HiOutlineXMark} boxSize="17px" />
        </Flex>

        <Flex align="center" gap="8px" wrap="wrap" pr="40px" mb="18px">
          <Chip tone="ink" h="28px" fontSize="12.5px">
            {KIND_LABEL[activity.kind]}
            {activity.kind === "QUIZ_RACE" && !showResults ? ` · Question ${activity.current_index + 1} of ${activity.questions.length}` : ""}
          </Chip>
          {activity.kind === "ESCAPE_ROOM" && (
            <Chip h="28px" fontSize="12.5px">
              {activity.locks.filter((l) => l.cracked).length} of {activity.locks.length} locks cracked
            </Chip>
          )}
          <Box flex="1" />
          {!showResults && (
            <Flex direction="column" align="center" px="16px" py="8px" borderRadius="16px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
              <Text fontSize="10px" letterSpacing="0.12em" color={c.textMuted}>ANSWERED</Text>
              <Text fontSize="24px" fontWeight="700" color={c.text} sx={{ fontVariantNumeric: "tabular-nums" }}>
                {answered}/{activity.class_size}
              </Text>
            </Flex>
          )}
          {!showResults && activity.kind === "QUIZ_RACE" && <Countdown activity={activity} clock={clock} size="lg" />}
        </Flex>

        {showResults ? (
          <ClassPicture activity={activity} act={act} onReteach={onReteach} onClear={onClear} />
        ) : activity.kind === "ESCAPE_ROOM" ? (
          <EscapeBoard
            activity={activity}
            unlockError={unlockError}
            onUnlock={async (code) => {
              setUnlockError(null);
              const ok = await act((api, s, id) => api.unlock(s, id, code));
              if (!ok) setUnlockError("That code does not open the door.");
            }}
          />
        ) : activity.kind === "QUIZ_RACE" ? (
          <Box display="grid" gridTemplateColumns={{ base: "1fr", lg: "1.6fr 1fr" }} gap="24px">
            <Box>{current && <QuestionBody question={current} size="lg" />}</Box>
            <Leaderboard rows={activity.leaderboard} />
          </Box>
        ) : (
          <Flex direction="column" gap="10px">
            {activity.questions.map((q, i) => {
              const n = activity.answered_counts[q.guid] || 0;
              return (
                <Box key={q.guid} p="14px 16px" borderRadius="14px" bg={c.surface} border="1px solid" borderColor={c.borderSoft}>
                  <Flex justify="space-between" gap="12px">
                    <Text fontSize="15px" color={c.text} noOfLines={2}>{i + 1}. {q.question_text}</Text>
                    <Text fontSize="15px" fontWeight="700" color={c.text} flexShrink={0}>{n}/{activity.class_size}</Text>
                  </Flex>
                  <Box mt="8px" h="6px" borderRadius="200px" bg={c.surfaceSubtle} overflow="hidden">
                    <Box h="100%" w={`${activity.class_size ? (100 * n) / activity.class_size : 0}%`} bg={STANDE.primaryBlue} transition="width 0.4s" />
                  </Box>
                </Box>
              );
            })}
          </Flex>
        )}
        {error && !showResults && <Text fontSize="12.5px" color={c.accentSoftText} mt="10px">{error}</Text>}
      </Box>
    </Flex>
  );
};

export default ActivityStage;
